import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import { useNavigate } from 'react-router-dom';
import AppTheme from '../shared-theme/AppTheme';
import { CustomCard, CustomContainer } from '../shared-theme/BasicComponentTheme';
import McdcLogo from '../../assets/images/logo-mcdc.png';
import ClemsonLogo from '../../assets/images/logo-clemson.png';
import { useAuthManager } from "../../hooks/useAuthHook";

export default function ResetPassword(props: { disableCustomTheme?: boolean }) {
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [token, setToken] = useState(''); // Token for password reset

  const {useResetPassword} = useAuthManager()
  const { mutate: resetPassword } = useResetPassword();
  const navigate = useNavigate();

  const validateInputs = (): boolean => {
    const newPassword = (document.getElementById('new-password') as HTMLInputElement).value;
    const confirmPassword = (document.getElementById('password') as HTMLInputElement).value;

    let isValid = true;

    if (!newPassword || newPassword.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError(true);
      setConfirmPasswordErrorMessage('Passwords do not match.');
      isValid = false;
    } else {
      setConfirmPasswordError(false);
      setConfirmPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateInputs()) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token") as string;


    const newPassword = (document.getElementById('new-password') as HTMLInputElement).value;

    resetPassword(
      { token: token, password: newPassword },
      {
        onSuccess: () => {
          setSuccessMessage('Password reset successfully. Redirecting to Sign In...');
          setErrorMessage('');
          setTimeout(() => navigate('/signin'), 3000); // Redirect after a delay
        },
        onError: (error: any) => {
          setErrorMessage('Failed to reset the password. Please try again.');
          setSuccessMessage('');
          // console.error('Password reset error:', error);
        },
      }
    );
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <CustomContainer direction="column" justifyContent="center">
        <CustomCard variant="outlined">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <img
              src={McdcLogo}
              alt="MCDC Logo"
              style={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'contain' }}
            />
            <img
              src={ClemsonLogo}
              alt="Clemson Logo"
              style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'contain' }}
            />
          </Box>
          <Typography component="h1" variant="h4" sx={{ width: '100%', fontSize: 'clamp(1rem, 5vw, 2.15rem)' }}>
            Set a new password
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
          >
            <FormControl>
              <FormLabel htmlFor="new-password">New Password</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                id="new-password"
                name="new-password"
                placeholder="••••••"
                type="password"
                autoFocus
                required
                fullWidth
                variant="outlined"
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Confirm Password</FormLabel>
              <TextField
                error={confirmPasswordError}
                helperText={confirmPasswordErrorMessage}
                id="password"
                name="password"
                placeholder="••••••"
                type="password"
                required
                fullWidth
                variant="outlined"
              />
            </FormControl>
            {successMessage && (
              <Typography color="success.main" align="center">
                {successMessage}
              </Typography>
            )}
            {errorMessage && (
              <Typography color="error.main" align="center">
                {errorMessage}
              </Typography>
            )}
            <Button type="submit" fullWidth variant="contained">
              Save
            </Button>
            <Divider>or</Divider>
            <Link
              component="button"
              type="button"
              variant="body2"
              sx={{ alignSelf: 'center' }}
              onClick={() => navigate('/signin')}
            >
              Sign In?
            </Link>
          </Box>
        </CustomCard>
      </CustomContainer>
    </AppTheme>
  );
}
